import {Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {usePrevious} from '../../../../../../srcCommon/helpers/hookHelpers';
import {Dialog} from '../../../../../components/common/dialogs/dialog';
import {DialogContent} from '../../../../../components/common/dialogs/dialogContent';
import {DialogFooter} from '../../../../../components/common/dialogs/dialogFooter';
import {DialogHeader} from '../../../../../components/common/dialogs/dialogHeader';
import {globalNamespace} from '../../../../../helpers/models/namespaceHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {isShallowEqual} from '../../../../../helpers/reselectHelpers';
import {IntegrationModel, IntegrationSettingsTypesEnum} from '../../../../../models/api/integrationModel';
import {IntegrationTypeModel} from '../../../../../models/api/integrationTypesModel';
import {LegacyApplicationModel} from '../../../../../models/api/legacyApplicationModel';
import {SettingsAppsAppDetailsTabsEnum} from '../../../../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../services/tracking';
import {CreateUpdateIntegrationAsyncActionPayload} from '../../../../../store/data/integrations/integrationsActions';
import {getIsUserGlobalAdmin} from '../../../../../store/data/teammates/teammatesSelectors';
import {SettingsAppsConfigureAsana} from './asana/settingsAppsConfigureAsana';
import {SettingsAppsConfigureFullcontact} from './fullcontact/settingsAppsConfigureFullcontact';
import {SettingsAppsConfigureGithub} from './github/settingsAppsConfigureGithub';
import {SettingsAppsConfigureHipchat} from './hipchat/settingsAppsConfigureHipchat';
import {SettingsAppsConfigureHubspot} from './hubspot/settingsAppsConfigureHubspot';
import {SettingsAppsConfigureIntercom} from './intercom/settingsAppsConfigureIntercom';
import {SettingsAppsConfigureJira} from './jira/settingsAppsConfigureJira';
import {SettingsAppsConfigureMicrosoftDynamics} from './microsoftdynamics/settingsAppsConfigureMicrosoftDynamics';
import {SettingsAppsConfigureNicereply} from './nicereply/settingsAppsConfigureNicereply';
import {SettingsAppsConfigurePagerDuty} from './pagerduty/settingsAppsConfigurePagerDuty';
import {SettingsAppsConfigurePipedrive} from './pipedrive/settingsAppsConfigurePipedrive';
import {SettingsAppsConfigureSalesforceContainer} from './salesforce/settingsAppsConfigureSalesforceContainer';
import {SettingsAppsConfigureShopify} from './shopify/settingsAppsConfigureShopify';
import {SettingsAppsConfigureSlack} from './slack/settingsAppsConfigureSlack';
import {SettingsAppsConfigureSmooch} from './smooch/settingsAppsConfigureSmooch';
import {SettingsAppsConfigureTrello} from './trello/settingsAppsConfigureTrello';
import {SettingsAppsConfigureWebhooks} from './webhooks/settingsAppsConfigureWebhooks';

/*
 * Constants.
 */

const dialogWidth = 420; // px.
const appsToHideSecretKeyFor = [IntegrationSettingsTypesEnum.ASANA, IntegrationSettingsTypesEnum.JIRA];

/*
 * Props.
 */

interface SettingsAppsConfigureAppDialogProps {
  integrationType: IntegrationTypeModel;
  integration?: IntegrationModel;
  application?: LegacyApplicationModel;
  isIntegrationEnabled: boolean;

  onRequestClose: (detailsPage?: SettingsAppsAppDetailsTabsEnum) => void;
  updateIntegrationAsync: (
    integrationId: number,
    payload: CreateUpdateIntegrationAsyncActionPayload,
  ) => Promise<void>;
  createIntegrationAsync: (
    integrationType: IntegrationTypeModel,
    payload: CreateUpdateIntegrationAsyncActionPayload,
  ) => Promise<void>;
}

/*
 * Style.
 */

const StyledSecretKeyExplainerDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

/*
 * Component.
 */

export const SettingsAppsConfigureAppDialog: FC<SettingsAppsConfigureAppDialogProps> = (props) => {
  const {
    integration,
    application,
    integrationType,
    isIntegrationEnabled,
    onRequestClose,
    updateIntegrationAsync,
    createIntegrationAsync,
  } = props;
  const [isSaveEnabled, setIsSaveEnabled] = useState(true);
  const [configurationSettings, setConfigurationSettings] = useState<object | undefined>(
    integration?.settings,
  );
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);

  // Watch the settings for updates.
  const previousSettings = usePrevious(integration?.settings);
  useEffect(() => {
    if (isShallowEqual(integration?.settings, previousSettings)) {
      return;
    }
    setConfigurationSettings(integration?.settings);
  }, [integration?.settings, previousSettings]);

  const onSaveOrEnableClick = async () => {
    const wasIntegrationEnabled = integration?.isEnabled;

    // If we do not have an integration but we can enable the app we will have to create the integration.
    // We do not support passing settings here as after they enable the integration we will potentially
    // open the config.
    if (!integration) {
      await createIntegrationAsync(integrationType, {isEnabled: true, settings: configurationSettings});
    } else {
      await updateIntegrationAsync(integration.id, {isEnabled: true, settings: configurationSettings});
    }

    // If the integration was not enabled, track this as we completed the enabling otherwise we are updating.
    if (!wasIntegrationEnabled) {
      trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_ENABLE_COMPLETED, {
        integration_id: integration?.id,
        integration_type: integrationType.id,
      });
    } else {
      trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_UPDATE_COMPLETED, {
        integration_id: integration?.id,
        integration_type: integrationType.id,
      });
    }

    // Navigate to getting started if the app is not enabled.
    onRequestClose(!integration?.isEnabled ? SettingsAppsAppDetailsTabsEnum.GETTING_STARTED : undefined);
  };

  return (
    <Dialog width={dialogWidth} onRequestClose={() => onRequestClose()}>
      <DialogHeader
        title={renderTitle(integrationType, isIntegrationEnabled)}
        onRequestClose={() => onRequestClose()}
      />
      <DialogContent>
        {maybeRenderSecretKeyField(integration, application)}
        {maybeRenderAdditionalInformation(
          integrationType,
          integration,
          configurationSettings,
          setIsSaveEnabled,
          setConfigurationSettings,
        )}
      </DialogContent>
      <DialogFooter>
        <ButtonGroupSpacer />
        <ButtonGroup>
          <Button onClick={() => onRequestClose()}>
            <Trans id="settings-apps-configure-app-dialog-cancel-button">Cancel</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            onClick={onSaveOrEnableClick}
            isDisabled={!isSaveEnabled || !isGlobalAdmin}
          >
            {renderPrimaryActionLabel(integrationType, isIntegrationEnabled)}
          </Button>
        </ButtonGroup>
      </DialogFooter>
    </Dialog>
  );
};

/*
 * Helpers.
 */

function renderTitle(integrationType: IntegrationTypeModel, isIntegrationEnabled: boolean) {
  if (integrationType.isConfigurationRequiredToEnable && !isIntegrationEnabled) {
    return <Trans id="settings-apps-configure-app-dialog-enable-header">Configure and enable app</Trans>;
  }
  return <Trans id="settings-apps-configure-app-dialog-header">Configure app</Trans>;
}

function renderPrimaryActionLabel(integrationType: IntegrationTypeModel, isIntegrationEnabled: boolean) {
  if (integrationType.isConfigurationRequiredToEnable && !isIntegrationEnabled) {
    return <Trans id="settings-apps-configure-app-dialog-primary-button-enable">Enable</Trans>;
  }
  return <Trans id="settings-apps-configure-app-dialog-primary-button-save">Save</Trans>;
}

function maybeRenderSecretKeyField(integration?: IntegrationModel, application?: LegacyApplicationModel) {
  const authSecret =
    integration?.plugin?.authSecret ||
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    (application?.namespacePreferences[globalNamespace]?.preferences as any)?.authSecret;
  if (
    !authSecret ||
    !integration ||
    (integration.customSettingsType && appsToHideSecretKeyFor.includes(integration.customSettingsType))
  ) {
    return null;
  }
  return (
    <>
      <TextBox
        label={<Trans id="settings-apps-configure-app-dialog-secert-key">Secret key</Trans>}
        value={authSecret}
        isReadOnly
      />
      <StyledSecretKeyExplainerDiv>
        <Trans id="settings-apps-configure-app-dialog-secert-key-explainer">
          This secret key is used to authenticate your Front organization with {integration.name}&apos;s
          plugin.
        </Trans>
      </StyledSecretKeyExplainerDiv>
    </>
  );
}

function maybeRenderAdditionalInformation(
  integrationType: IntegrationTypeModel,
  integration: IntegrationModel | undefined,
  configurationSettings: any | undefined,
  onIsSaveEnabled: HandlerOf<boolean>,
  setConfigurationSettings: HandlerOf<object | undefined>,
) {
  switch (integrationType.id) {
    case IntegrationSettingsTypesEnum.ASANA:
      return <SettingsAppsConfigureAsana configurationSettings={configurationSettings} />;
    case IntegrationSettingsTypesEnum.FULLCONTACT:
      return (
        <SettingsAppsConfigureFullcontact
          integration={
            integration?.customSettingsType === IntegrationSettingsTypesEnum.FULLCONTACT
              ? integration
              : undefined
          }
          configurationSettings={configurationSettings}
          onIsSaveEnabled={onIsSaveEnabled}
          setConfigurationSettings={setConfigurationSettings}
        />
      );
    case IntegrationSettingsTypesEnum.GITHUB:
      return <SettingsAppsConfigureGithub configurationSettings={configurationSettings} />;
    case IntegrationSettingsTypesEnum.HIPCHAT:
      return (
        <SettingsAppsConfigureHipchat
          integration={
            integration?.customSettingsType === IntegrationSettingsTypesEnum.HIPCHAT ? integration : undefined
          }
          configurationSettings={configurationSettings}
          onIsSaveEnabled={onIsSaveEnabled}
          setConfigurationSettings={setConfigurationSettings}
        />
      );
    case IntegrationSettingsTypesEnum.HUBSPOT:
      return (
        <SettingsAppsConfigureHubspot
          integration={
            integration?.customSettingsType === IntegrationSettingsTypesEnum.HUBSPOT ? integration : undefined
          }
        />
      );
    case IntegrationSettingsTypesEnum.INTERCOM:
      return <SettingsAppsConfigureIntercom configurationSettings={configurationSettings} />;
    case IntegrationSettingsTypesEnum.JIRA:
      return (
        <SettingsAppsConfigureJira
          configurationSettings={configurationSettings}
          onIsSaveEnabled={onIsSaveEnabled}
          setConfigurationSettings={setConfigurationSettings}
        />
      );
    case IntegrationSettingsTypesEnum.MICROSOFT_DYNAMICS:
      return <SettingsAppsConfigureMicrosoftDynamics />;
    case IntegrationSettingsTypesEnum.NICEREPLY:
      return (
        <SettingsAppsConfigureNicereply
          configurationSettings={configurationSettings}
          onIsSaveEnabled={onIsSaveEnabled}
          setConfigurationSettings={setConfigurationSettings}
        />
      );
    case IntegrationSettingsTypesEnum.PAGERDUTY:
      return <SettingsAppsConfigurePagerDuty configurationSettings={configurationSettings} />;
    case IntegrationSettingsTypesEnum.PIPEDRIVE:
      return <SettingsAppsConfigurePipedrive configurationSettings={configurationSettings} />;
    case IntegrationSettingsTypesEnum.SALESFORCE:
      return (
        <SettingsAppsConfigureSalesforceContainer
          integration={
            integration?.customSettingsType === IntegrationSettingsTypesEnum.SALESFORCE
              ? integration
              : undefined
          }
        />
      );
    case IntegrationSettingsTypesEnum.SHOPIFY:
      return (
        <SettingsAppsConfigureShopify
          integration={
            integration?.customSettingsType === IntegrationSettingsTypesEnum.SHOPIFY ? integration : undefined
          }
          configurationSettings={configurationSettings}
          onIsSaveEnabled={onIsSaveEnabled}
          setConfigurationSettings={setConfigurationSettings}
        />
      );
    case IntegrationSettingsTypesEnum.SLACK:
      return (
        <SettingsAppsConfigureSlack
          configurationSettings={configurationSettings}
          setConfigurationSettings={setConfigurationSettings}
        />
      );
    case IntegrationSettingsTypesEnum.SMOOCH:
      return (
        <SettingsAppsConfigureSmooch
          integration={
            integration?.customSettingsType === IntegrationSettingsTypesEnum.SMOOCH ? integration : undefined
          }
          configurationSettings={configurationSettings}
          onIsSaveEnabled={onIsSaveEnabled}
          setConfigurationSettings={setConfigurationSettings}
        />
      );
    case IntegrationSettingsTypesEnum.TRELLO:
      return <SettingsAppsConfigureTrello configurationSettings={configurationSettings} />;
    case IntegrationSettingsTypesEnum.WEBHOOKS:
      return (
        <SettingsAppsConfigureWebhooks
          configurationSettings={configurationSettings}
          setConfigurationSettings={setConfigurationSettings}
        />
      );
    default:
      return null;
  }
}
